import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, useAuthStore } from '@dukene/shared';
import { Store, Store as StoreIcon, ArrowRight, Loader2 } from 'lucide-react';

function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) {
    const base = (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:3000/api/v1';
    let origin = '';
    try {
      origin = new URL(base).origin;
    } catch {
      origin = window.location.origin;
    }
    return `${origin}${url}`;
  }
  return url;
}

export default function SelectStore() {
  const navigate = useNavigate();
  const { pendingStoreSelection, finalizeStoreSelection, isAuthenticated } = useAuthStore();
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const {
    data: accessibleStores,
    isLoading: isLoadingStores,
    isError: failedToLoadStores,
  } = useQuery<any[]>({
    queryKey: ['select-store-access'],
    queryFn: () => apiClient.get<any[]>('/stores/merchant/stores'),
    enabled: !!pendingStoreSelection,
    retry: 1,
  });

  useEffect(() => {
    if (isAuthenticated && !pendingStoreSelection) {
      navigate('/');
    } else if (!pendingStoreSelection) {
      navigate('/login');
    }
  }, [pendingStoreSelection, isAuthenticated, navigate]);

  if (!pendingStoreSelection) return null;

  const stores = accessibleStores || [];

  const handleSelect = (storeId: string) => {
    if (!stores.some((store) => store.id === storeId)) return;
    setSelectedStore(storeId);
    setTimeout(() => {
      finalizeStoreSelection(storeId);
      navigate('/');
    }, 500);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white font-sans selection:bg-black selection:text-white px-4 sm:px-6 lg:px-8 py-12">
      <div className="w-full max-w-md mx-auto">
        
        {/* Branding Header */}
        <div className="flex flex-col items-center justify-center mb-12">
          <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6">
            <Store className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-4xl font-light text-black tracking-tight mb-2 text-center">
            Welcome Back
          </h2>
          <p className="text-gray-500 text-base font-light text-center">
            Hi {pendingStoreSelection.user.firstName}, select a store to continue
          </p>
        </div>

        {/* Store List */}
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
          {isLoadingStores && (
            <div className="flex items-center justify-center py-10 text-sm text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Checking store access...
            </div>
          )}
          {failedToLoadStores && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              We could not verify your store access. Please return to login and try again.
            </div>
          )}
          {!isLoadingStores && !failedToLoadStores && stores.length === 0 && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              You do not currently have access to an active store.
            </div>
          )}
          {!isLoadingStores && !failedToLoadStores && stores.map((store: any) => (
            <div
              key={store.id}
              onClick={() => handleSelect(store.id)}
              className={`group relative flex items-center space-x-4 p-4 cursor-pointer rounded-xl transition-all duration-200 border ${
                selectedStore === store.id 
                  ? 'border-black bg-gray-50' 
                  : 'border-transparent hover:border-gray-200 bg-white hover:bg-gray-50/50'
              }`}
            >
              <div className="flex-shrink-0">
                {store.logo ? (
                  <img className="h-12 w-12 rounded-lg object-cover border border-gray-100" src={resolveImageUrl(store.logo) as string} alt={store.name} />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center">
                    <StoreIcon className="h-6 w-6 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className={`truncate text-base font-medium ${selectedStore === store.id ? 'text-black' : 'text-gray-900'}`}>
                  {store.name || 'Unnamed Store'}
                </p>
                <p className={`truncate text-sm font-light ${selectedStore === store.id ? 'text-gray-600' : 'text-gray-500'}`}>
                  {store.subdomain ? `${store.subdomain}.dukene.com` : 'No subdomain set'}
                </p>
              </div>
              <div className="pl-2">
                <ArrowRight className={`h-5 w-5 transition-all duration-200 ${
                  selectedStore === store.id 
                    ? 'text-black translate-x-1' 
                    : 'text-gray-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:text-gray-600'
                }`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
